import { useEffect, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import { useTemplateRetrieveQuery } from '@/services/api/generated/templatesV2Service';

interface TemplateRowPreviewCardProps {
  templateId: string;
  templateName: string;
  previewRenderUrl?: string | null;
  previewImageUrl?: string | null;
  sampleRenderStatus?: string | null;
}

const isImageUrl = (url: string) =>
  /\.(png|jpe?g|gif|webp|svg)(\?.*)?$/i.test(url);

const loadedPreviewUrls = new Set<string>();

export const TemplateRowPreviewCard = ({
  templateId,
  templateName,
  previewRenderUrl,
  previewImageUrl: hintedPreviewImageUrl,
  sampleRenderStatus,
}: TemplateRowPreviewCardProps) => {
  const hintedPreviewUrl = useMemo(() => {
    if (hintedPreviewImageUrl) return hintedPreviewImageUrl;
    if (previewRenderUrl && isImageUrl(previewRenderUrl)) {
      return previewRenderUrl;
    }
    return null;
  }, [hintedPreviewImageUrl, previewRenderUrl]);

  const hintedStatus = useMemo(
    () => String(sampleRenderStatus ?? ''),
    [sampleRenderStatus],
  );

  const shouldFetchTemplate =
    !hintedPreviewUrl &&
    hintedStatus !== 'QUEUED' &&
    hintedStatus !== 'RENDERING';

  const { data, isFetching } = useTemplateRetrieveQuery(
    { id: templateId },
    {
      skip: !shouldFetchTemplate,
    },
  );

  const resolvedPreviewImageUrl = useMemo(() => {
    if (hintedPreviewUrl) {
      return hintedPreviewUrl;
    }
    if (data?.preview_image_url) return data.preview_image_url;
    if (data?.preview_render_url && isImageUrl(data.preview_render_url)) {
      return data.preview_render_url;
    }
    return null;
  }, [data?.preview_image_url, data?.preview_render_url, hintedPreviewUrl]);

  const effectiveStatus = useMemo(() => {
    if (hintedStatus) {
      return hintedStatus;
    }
    return String(data?.sample_render_status ?? '');
  }, [data?.sample_render_status, hintedStatus]);

  const isRenderingPreview =
    effectiveStatus === 'QUEUED' || effectiveStatus === 'RENDERING';

  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!resolvedPreviewImageUrl) {
      setImageLoaded(false);
      return undefined;
    }

    if (loadedPreviewUrls.has(resolvedPreviewImageUrl)) {
      setImageLoaded(true);
      return undefined;
    }

    setImageLoaded(false);
    let cancelled = false;

    const image = new Image();
    image.decoding = 'async';
    image.src = resolvedPreviewImageUrl;
    image.onload = () => {
      if (cancelled) {
        return;
      }
      loadedPreviewUrls.add(resolvedPreviewImageUrl);
      setImageLoaded(true);
    };
    image.onerror = () => {
      if (cancelled) {
        return;
      }
      setImageLoaded(true);
    };

    return () => {
      cancelled = true;
      image.onload = null;
      image.onerror = null;
    };
  }, [resolvedPreviewImageUrl]);

  return (
    <div className="w-[360px] overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-xl dark:border-zinc-700 dark:bg-zinc-900">
      <div className="border-b border-zinc-200 px-3 py-2 dark:border-zinc-700">
        <p
          className="truncate text-xs font-semibold text-zinc-800 dark:text-zinc-200"
          title={templateName}
        >
          {templateName}
        </p>
      </div>

      {isFetching && !resolvedPreviewImageUrl && (
        <div className="flex h-[240px] flex-col items-center justify-center gap-2 bg-zinc-100 text-xs font-medium text-zinc-500 dark:bg-zinc-800 dark:text-zinc-300">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-300 border-t-zinc-500 dark:border-zinc-600 dark:border-t-zinc-300" />
          Loading preview...
        </div>
      )}

      {resolvedPreviewImageUrl && (
        <div className="relative h-[240px] w-full overflow-hidden bg-zinc-100 dark:bg-zinc-800">
          {!imageLoaded && (
            <div className="absolute inset-0 animate-pulse bg-zinc-100 dark:bg-zinc-800" />
          )}
          <img
            src={resolvedPreviewImageUrl}
            alt={`${templateName} preview`}
            className={cn(
              'h-[240px] w-full object-contain object-top transition-opacity duration-150',
              imageLoaded ? 'opacity-100' : 'opacity-0',
            )}
            decoding="async"
            loading="lazy"
            fetchPriority="auto"
            onLoad={() => {
              if (resolvedPreviewImageUrl) {
                loadedPreviewUrls.add(resolvedPreviewImageUrl);
              }
              setImageLoaded(true);
            }}
            onError={() => setImageLoaded(true)}
          />
        </div>
      )}

      {!isFetching && !resolvedPreviewImageUrl && isRenderingPreview && (
        <div className="flex h-[240px] items-center justify-center px-4 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400">
          Preview is rendering. Check back in a moment.
        </div>
      )}

      {!isFetching && !resolvedPreviewImageUrl && !isRenderingPreview && (
        <div className="flex h-[240px] items-center justify-center px-4 text-center text-xs font-medium text-zinc-500 dark:text-zinc-400">
          No preview image available for this template.
        </div>
      )}
    </div>
  );
};
